import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Search, Download, FileText, FileCheck2, FileX2, Users, Building2 } from "lucide-react";

const NAVY = "#0F2C59";

const Chip = ({ label, value, color }) => (
  <div className="rounded-md border border-border bg-muted/40 px-2.5 py-1.5">
    <p className="text-[11px] text-muted-foreground leading-none">{label}</p>
    <p className="text-base font-bold tabular-nums leading-tight mt-1" style={{ color }}>{value}</p>
  </div>
);

export const PersonDocSearch = () => {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (term.length < 2) { setData(null); return; }
    setLoading(true);
    api.get("/yetki/person-search", { params: { q: term } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [term]);

  const download = async (format) => {
    setBusy(true);
    try {
      const res = await api.get("/yetki/person-search/export", {
        params: { format, q: term }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${term.toUpperCase()} YETKİ BELGESİ ARAMA.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally { setBusy(false); }
  };

  const people = data?.people || [];
  const firms = data?.firms || [];
  const hasAny = people.length > 0 || firms.length > 0;

  return (
    <div className="space-y-4" data-testid="person-doc-search">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8 h-10" placeholder="Yetkili ismi, firma adı veya sicil no..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="pds-input" autoFocus />
        </div>
        {term.length >= 2 && hasAny && (
          <>
            <Button variant="outline" size="sm" className="h-10" disabled={busy}
              onClick={() => download("xlsx")} data-testid="pds-export-xlsx">
              <Download className="h-4 w-4 mr-1.5" />Excel
            </Button>
            <Button variant="outline" size="sm" className="h-10" disabled={busy}
              onClick={() => download("pdf")} data-testid="pds-export-pdf">
              <FileText className="h-4 w-4 mr-1.5" />PDF
            </Button>
          </>
        )}
      </div>

      {term.length < 2 ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="pds-hint">
          Oy kullanacak yetkilinin ismini, firma adını veya sicil numarasını yazın — kaç oyu var, hangi komitelerde ve belgeler kimde, anında listelenir.
        </p>
      ) : loading ? (
        <Skeleton className="h-40 w-full" />
      ) : !hasAny ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="pds-empty">
          “{term}” ile eşleşen yetkili veya firma bulunamadı.
        </p>
      ) : (
        <>
          {people.length > 0 && (
          <>
          <p className="text-sm text-muted-foreground" data-testid="pds-count">
            <span className="font-bold" style={{ color: NAVY }}>{data.person_count}</span> kişi ·{" "}
            <span className="font-bold" style={{ color: NAVY }}>{data.total}</span> oy ·{" "}
            <span className="font-bold text-emerald-600">{data.belge}</span> belge alınmış
          </p>
          {people.map((p) => (
            <div key={p.person} className="rounded-lg border border-border overflow-hidden"
              data-testid={`pds-person-${p.person}`}>
              <div className="flex items-center gap-3 flex-wrap p-3 bg-muted/50 border-b border-border">
                <Users className="h-4 w-4 shrink-0" style={{ color: NAVY }} />
                <p className="font-bold text-base" data-testid={`pds-name-${p.person}`}>{p.person}</p>
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                  <Chip label="Toplam oy" value={p.total} color={NAVY} />
                  <Chip label="Belge alınmış" value={p.belge} color="#16A34A" />
                  <Chip label="Belge eksik" value={p.belge_yok} color="#DC2626" />
                  <Chip label="Komite" value={p.committees.length} color="#7C3AED" />
                </div>
              </div>
              <div className="px-3 pt-2 text-xs text-muted-foreground">
                Komiteler: {p.committees.map((c) => `#${c}`).join(" · ") || "—"}
                {p.konum_bos > 0 && (
                  <span className="text-amber-600 font-medium"> · {p.konum_bos} belgenin yeri girilmemiş</span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Firma</th>
                      <th className="px-3 py-2 font-medium">Komite</th>
                      <th className="px-3 py-2 font-medium">Belge</th>
                      <th className="px-3 py-2 font-medium">Belge Kimde / Nerede</th>
                      <th className="px-3 py-2 font-medium">Şube</th>
                      <th className="px-3 py-2 font-medium">2026</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.firms.map((x) => (
                      <tr key={x.id} className="border-t border-border/60" data-testid={`pds-firm-${x.id}`}>
                        <td className="px-3 py-2 font-medium">{x.company_name}
                          {x.sicil_no && <span className="text-muted-foreground font-normal"> · {x.sicil_no}</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="text-muted-foreground">#{x.committee_no}</span> {x.committee_name}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {x.proxy_document ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                              <FileCheck2 className="h-3.5 w-3.5" />Alındı
                              {x.proxy_document_date && <span className="text-muted-foreground font-normal text-xs">{x.proxy_document_date}</span>}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                              <FileX2 className="h-3.5 w-3.5" />Yok
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {x.document_holder
                            ? <span className="font-medium">{x.document_holder}</span>
                            : <span className="text-amber-600">girilmemiş</span>}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{x.branch || "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{x.prediction || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          </>
          )}

          {firms.length > 0 && (
            <div className="rounded-lg border border-border overflow-hidden" data-testid="pds-firm-section">
              <div className="flex items-center gap-3 flex-wrap p-3 bg-muted/50 border-b border-border">
                <Building2 className="h-4 w-4 shrink-0 text-cyan-600" />
                <p className="font-bold text-base">Firma Eşleşmeleri</p>
                <span className="text-xs text-muted-foreground" data-testid="pds-firm-count">
                  {data.firm_count} firma · {data.firm_belge} belge alınmış
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Firma</th>
                      <th className="px-3 py-2 font-medium">Komite</th>
                      <th className="px-3 py-2 font-medium">Oy Kullanacak Yetkili</th>
                      <th className="px-3 py-2 font-medium">Belge</th>
                      <th className="px-3 py-2 font-medium">Belge Kimde / Nerede</th>
                      <th className="px-3 py-2 font-medium">Şube</th>
                      <th className="px-3 py-2 font-medium">2026</th>
                    </tr>
                  </thead>
                  <tbody>
                    {firms.map((x) => (
                      <tr key={x.id} className="border-t border-border/60" data-testid={`pds-firmrow-${x.id}`}>
                        <td className="px-3 py-2 font-medium">{x.company_name}
                          {x.sicil_no && <span className="text-muted-foreground font-normal"> · {x.sicil_no}</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="text-muted-foreground">#{x.committee_no}</span> {x.committee_name}
                        </td>
                        <td className="px-3 py-2">
                          {x.voting_person
                            ? <span className="font-semibold" style={{ color: NAVY }}>{x.voting_person}</span>
                            : <span className="text-amber-600">belirlenmemiş</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {x.proxy_document ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                              <FileCheck2 className="h-3.5 w-3.5" />Alındı
                              {x.proxy_document_date && <span className="text-muted-foreground font-normal text-xs">{x.proxy_document_date}</span>}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                              <FileX2 className="h-3.5 w-3.5" />Yok
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {x.document_holder
                            ? <span className="font-medium">{x.document_holder}</span>
                            : <span className="text-amber-600">girilmemiş</span>}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{x.branch || "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{x.prediction || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PersonDocSearch;
